import React, { useState } from 'react';
import { capitalize } from 'lodash';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal from 'components/shared/StandardModal';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import type { AIPrompt } from 'models/flexible/AIPrompt';
import { updateAIPrompt } from 'services/api/admin/aiPrompts';
import { adminIndexClients } from 'services/api/admin/clients';
import type { AdminAIPromptIndexFields, AdminAIPromptShowFields } from 'services/api/fields';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

type Props = {
  isOpen: boolean;
  aiPrompt:
    | AIPrompt<AdminAIPromptShowFields>
    | AIPrompt<
        AdminAIPromptIndexFields<
          | 'client'
          | 'groupTypes'
          | 'planTypeClassifications'
          | 'episodeClassifications'
          | 'config'
          | 'latestProcessPromptStatus'
        >
      >
    | null;
  onCancel: () => void;
  onComplete: () => void;
};

export default function AIPromptPublishModal({ isOpen, aiPrompt, onCancel, onComplete }: Props) {
  const [client, setClient] = useState(aiPrompt?.client ?? null);
  const [submitting, setSubmitting] = useState(false);
  const { addToast } = useToastActions();

  const clientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'publishModal', { leaf: true }],
    fetchFn: adminIndexClients,
    params: { leaf: true, sortBy: 'name asc' },
  });

  if (!isOpen || !aiPrompt) return null;

  const handlePublish = async () => {
    setSubmitting(true);
    try {
      await updateAIPrompt(aiPrompt.id, {
        clientId: client?.id || '',
        isPublished: true,
      });
      addToast({ text: 'AI prompt published.' });
      onComplete();
    } catch {
      addToast({ text: 'There was an error publishing the AI prompt. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StandardModal title='Confirm Details to Publish' onCancel={onCancel}>
      <FormContainer>
        <IntroText>
          Select a client to assign this prompt. For prompts intended to be used as a fallback, do not assign a client.
        </IntroText>

        <InputGroup title='Client'>
          <Select
            {...clientOpts}
            value={client}
            onChange={(v) => setClient(v)}
            getOptionValue={(o) => o.id}
            getOptionLabel={(o) => o.name}
            placeholder='Select client'
            isClearable
          />
        </InputGroup>

        <ConfigSection>
          <SectionHeader>Review and confirm prompt configuration before publishing</SectionHeader>
          <SummaryRow>
            <SummaryLabel>Prompt Type</SummaryLabel>
            <SummaryValue>{capitalize(aiPrompt.type || '—')}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Levels of Care</SummaryLabel>
            <SummaryValue>{aiPrompt.groupTypes.map((gt: any) => gt.displayName).join(', ') || '—'}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Plan Types</SummaryLabel>
            <SummaryValue>{aiPrompt.planTypeClassifications.map((c: any) => c.name).join(', ') || '—'}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Episode Types</SummaryLabel>
            <SummaryValue>{aiPrompt.episodeClassifications.map((c: any) => c.name).join(', ') || '—'}</SummaryValue>
          </SummaryRow>
        </ConfigSection>
      </FormContainer>

      <Actions>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handlePublish} disabled={submitting} loading={submitting}>
          Publish Prompt
        </Button>
      </Actions>
    </StandardModal>
  );
}

const FormContainer = styled.div`
  margin-bottom: 24px;
`;

const IntroText = styled(BodySmallBold)`
  margin-bottom: 12px;
  display: block;
`;

const ConfigSection = styled.div`
  margin-top: 24px;
`;

const SectionHeader = styled(BodySmall)`
  margin-bottom: 12px;
  font-weight: 400;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid ${colors.black10};
`;

const SummaryLabel = styled(BodySmallBold)`
  color: ${colors.black50};
`;

const SummaryValue = styled.div`
  font-size: 14px;
`;

const Actions = styled(ButtonGroup)`
  justify-content: flex-end;
`;
